/**
 * @fileoverview API Utility Functions for React Application
 * 
 * This module provides essential utility functions for handling API responses, authentication,
 * error management, and retry logic in a React application. It includes:
 * 
 * - Authentication error handling with automatic redirects
 * - Advanced error message logging and user notification
 * - Response processing and validation
 * - Retry logic for failed requests with configurable parameters
 * 
 * The utilities are designed to work with a snackbar notification system and handle
 * various HTTP status codes including authentication failures, server errors, and timeouts.
 * 
  * @version 1.0.0
 */

import { MutableRefObject } from "react";

import { REACT_APP_LOGOUT_URL } from "config/config";
import { AUTH_REDIRECT_URL, MAX_RETRIES } from "config/constants";
import { handleApiError } from "utils/apiErrorHandling";
import { captureAuthErrorInSentry } from "utils/sentryErrorTracking";
import { isErrorStatusCode, isSuccessfulStatusCode, isTimeoutStatusCode } from "utils/statusCheck";

import { clearLastRefreshAt } from "./sessionRefresh/storage";
import { ApiError, BaseMutationOptions, BaseQueryOptions } from './types';

/** When set, suppresses all API requests/retries during auth redirect. Resets on full page load. */
let isLoggingOut = false;
export const getIsLoggingOut = (): boolean => isLoggingOut;
export const setIsLoggingOut = (): void => {
  isLoggingOut = true;
  // Wipe the cross-tab refresh timestamp so that post-logout 401 handlers cannot
  // treat a pre-logout refresh as "session still fresh" and skip a real refresh call.
  clearLastRefreshAt();
};

/**
 * Handles authentication-related HTTP errors and performs automatic redirects.
 * 
 * This function processes 401 (Unauthorized) and 419 (Session Expired) status codes,
 * displays appropriate user notifications, and redirects users to authentication pages.
 * 
 * @param response - The HTTP Response object to check for authentication errors
 * @param enqueueSnackbar - Function to display notifications to the user
 * @returns {boolean} - Returns true if an authentication error was handled, false otherwise
 * 
 * @example
 * ```typescript
 * const isAuthError = handleAuthenticationError(response, enqueueSnackbar);
 * if (isAuthError) {
 *   // User will be redirected, no further processing needed
 *   return;
 * }
 * ```
 */
export const handleAuthenticationError = (response: Response, enqueueSnackbar: any): boolean => {
    // Handle 419 status (session expired via service worker)
    // We convert "failed to fetch" into 419 using the sw.js file.
    // We get "failed to fetch" after a 302 to cognito and CORS failure.
    if (response.status === 419) {
        setIsLoggingOut();
        captureAuthErrorInSentry(response, AUTH_REDIRECT_URL);
        enqueueSnackbar("Your session has expired. Redirecting you to the login page ...", {
            variant: "info"
        });
        window.location.href = AUTH_REDIRECT_URL;
        return true;
    }

    // Handle 401 status (unauthorized)
    if (response.status === 401) {
        // Check for X-Auth-Redirect header which indicates where to redirect for authentication
        const authRedirectUrl = response.headers.get("X-Auth-Redirect");
        const redirectTarget = authRedirectUrl || REACT_APP_LOGOUT_URL;
        setIsLoggingOut();
        captureAuthErrorInSentry(response, redirectTarget);
        if (authRedirectUrl) {
            enqueueSnackbar("Your session has expired. Redirecting you to the login page ...", {
                variant: "info"
            });
        }
        // Redirect to the specified URL or fallback to logout URL
        window.location.href = redirectTarget;
        return true; // it indicates an auhentication error
    }

    return false; // it is not an authentication error
};


/**
 * Processes HTTP responses and handles authentication, errors, and data extraction.
 * 
 * This is a comprehensive response handler that:
 * - Checks for authentication errors and redirects if necessary
 * - Handles empty responses (204 No Content)
 * - Extracts JSON data from successful responses
 * - Throws appropriate errors for failed requests
 * 
 * @template T - The expected type of the response data
 * @param response - The HTTP Response object to process
 * @param enqueueSnackbar - Function to display notifications to the user
 * 
 * @returns {Promise<T>} - Promise resolving to the parsed response data
 * @throws {Error} - Throws an error with status code for failed requests
 * 
 * @example
 * ```typescript
 * try {
 *   const data = await handleResponse<UserData>(response, enqueueSnackbar);
 *   console.log("User data:", data);
 * } catch (error) {
 *   console.error("Request failed:", error.status);
 * }
 * ```
 */
export const handleResponse = async <T>(response: Response, enqueueSnackbar: any): Promise<T> => {
    // if the response is an authentication error, we redirect to the login page or logout page
    const isAuthError = handleAuthenticationError(response, enqueueSnackbar);

    // if it's an auth error, don't process further as redirect will happen
    if (isAuthError) {
        const error = new Error("Authentication error") as Error & { status?: number; data?: any };
        error.status = response.status;
        throw error;
    }

    // if the response is empty, we return an empty object
    if (response.status === 204) return {} as T;

    let result: any = {};
    const responseTemp = await response.clone();
    try {
        result = await responseTemp.json();
    } catch (error) {
        // do nothing
    }

    // if the response is not successful, we throw an error
    if (!response.ok) {

        const error = new Error(response.statusText || "An error occurred") as Error & { status?: number; data?: any };
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Determines whether a failed API request should be retried based on error conditions.
 * 
 * This function implements sophisticated retry logic that considers:
 * - HTTP status codes (success, client errors, server errors, timeouts)
 * - Component mount status to prevent unnecessary operations
 * - Maximum retry limits and failure counts
 * - Error notification display preferences
 * - Event logging for monitoring purposes
 * 
 * @template T - The expected type of the response data
 * @param options - Configuration options for the API request
 * @param error - The ApiError object containing error details
 * @param responseRef - Mutable reference to the HTTP Response object
 * @param enqueueSnackbar - Function to display error notifications
 * @param getIsMounted - Function to check if the component is still mounted
 * @param sendErrorEvent - Function to log error events for monitoring
 * @param failureCount - Current number of failed attempts
 * @param retry - Maximum number of retry attempts allowed
 * 
 * @returns {boolean} - Returns true if the request should be retried, false otherwise
 * 
 * @example
 * ```typescript
 * const shouldRetry = handleRetry(
 *   options,
 *   error,
 *   responseRef,
 *   enqueueSnackbar,
 *   getIsMounted,
 *   sendErrorEvent,
 *   failureCount,
 *   maxRetries
 * );
 * 
 * if (shouldRetry) {
 *   // Perform retry logic
 *   setTimeout(() => makeRequest(), 1000);
 * }
 * ```
 */
export const handleRetry = <T, Variables = unknown>(
    options: BaseQueryOptions<T> | BaseMutationOptions<T, Variables>,
    error: ApiError,
    responseRef: MutableRefObject<Response | undefined>,
    enqueueSnackbar: any,
    getIsMounted: () => boolean,
    sendErrorEvent: (eventText: string) => void,
    failureCount: number,
    retry: number
) => {

    // If the app is logging out or session expired, suppress all retries immediately
    if (isLoggingOut) return false;

    const canSeeSnackbarError =
        options?.shouldShowErrorSnackbar === undefined || options.shouldShowErrorSnackbar({ status: error.status });

    // Success response, no need to retry
    if (isSuccessfulStatusCode(error.status)) return false;

    if (isErrorStatusCode(error.status)) {
        if (!getIsMounted()) {
            return false;
        }
        if (canSeeSnackbarError && responseRef.current) {
            handleApiError(responseRef.current, enqueueSnackbar, {
                shouldShowSnackbar: true,
                isRetry: false,
            });
        }
        return false;
    }

    if (isTimeoutStatusCode(error.status)) {
        if (!getIsMounted()) {
            return false;
        }

        if (failureCount === MAX_RETRIES) {
            if (canSeeSnackbarError && responseRef.current) {
                handleApiError(responseRef.current, enqueueSnackbar, {
                    shouldShowSnackbar: true,
                    isRetry: true,
                    attempt: failureCount,
                    maxRetries: MAX_RETRIES,
                });
            }
            const eventText = `${responseRef.current?.url}`;
            sendErrorEvent(eventText);
        }
        return true; // Retry for server errors
    }

    if (failureCount === retry || failureCount === MAX_RETRIES) {
        return false;
    }
    return false;
}
